import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, PlusCircle, FileText, BarChart3, ShoppingCart, Headphones, Package, LogOut, User, Users, Settings, ClipboardList, Store, Globe, Shield, TrendingUp, Grid3X3, Ship, Truck, ArrowLeftRight, Sparkles, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChangePasswordGate, DiscrepancyLockGate, useDiscrepancyLock } from "@/components/AccountGates";

const ORION_LOGO = "https://customer-assets.emergentagent.com/job_614a1f6e-bdce-4d8d-9aef-4c923c15f759/artifacts/ju21ueiv_ORION%20LOGO.jpg";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/add", label: "Add RTS", icon: PlusCircle },
  { path: "/records", label: "RTS Records", icon: FileText },
  { path: "/reorders", label: "Re-orders", icon: ShoppingCart },
  { path: "/after-sales", label: "After-Sales", icon: Headphones },
  { path: "/reports", label: "Reports", icon: BarChart3 },
  { type: "divider", label: "Inventory" },
  { path: "/inventory", label: "All Stocks", icon: Package },
  { path: "/global-inventory", label: "Global Matrix", icon: Grid3X3 },
  { path: "/receiving", label: "Receiving", icon: Ship },
  { path: "/transfers", label: "Transfer Log", icon: ArrowLeftRight },
  { path: "/purchase-request", label: "Purchase Request", icon: ShoppingCart },
  { type: "divider", label: "Sales" },
  { path: "/walk-in-sales", label: "Walk-In Sales", icon: Store },
  { path: "/online-sales", label: "Online Sales", icon: Globe },
  { path: "/warranty", label: "Warranty", icon: Shield },
  { type: "divider", label: "Analytics" },
  { path: "/analytics", label: "Analytics", icon: TrendingUp },
  { path: "/sales-analytics", label: "Sales Dashboard", icon: BarChart3 },
];

const adminNavItems = [
  { type: "divider", label: "Admin" },
  { path: "/admin/users", label: "User Management", icon: Users },
  { path: "/admin/activity-logs", label: "Activity Logs", icon: ClipboardList },
  { path: "/admin/stock-audit", label: "Stock Audit", icon: ShieldAlert },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { lockData, recheck } = useDiscrepancyLock(user);

  // Combine nav items - add admin section only for admin users
  const allNavItems = user?.role === "admin" 
    ? [...navItems, ...adminNavItems] 
    : navItems;

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    navigate("/login");
  };

  // GATE 1 (highest priority): forced password change on first login / after admin reset
  if (user?.must_change_password) {
    return <ChangePasswordGate />;
  }

  // Wait for the discrepancy-lock status before mounting pages (avoids premature API calls)
  if (user && lockData === null) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white" data-testid="lock-check-loading">
        <div className="h-8 w-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // GATE 2: branch has OPEN discrepancy → user is locked until resolved (owners exempt)
  if (lockData?.locked) {
    return <DiscrepancyLockGate lockData={lockData} onResolved={recheck} />;
  }

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 sidebar z-50 no-print flex flex-col" data-testid="sidebar">
        <div className="p-4 border-b border-white/20 flex-shrink-0">
          <div className="flex items-center justify-center">
            <img 
              src={ORION_LOGO} 
              alt="ORION - The Light of Champions" 
              className="h-16 w-auto object-contain rounded-lg"
              data-testid="orion-logo"
            />
          </div>
        </div>
        
        {/* Scrollable Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto" data-testid="nav-menu">
          {(() => {
            const isAdmin = user?.role === "admin" || user?.is_admin === true || user?.can_access_all_branches === true;
            const hasBranch = !!user?.assigned_branch || (user?.assigned_branches?.length > 0);
            const isPendingAccess = user && !isAdmin && !hasBranch;
            if (isPendingAccess) {
              return (
                <div className="text-center py-8 px-3">
                  <Shield className="h-10 w-10 text-amber-300 mx-auto mb-2" />
                  <p className="text-xs text-white/70">
                    Naghihintay ka ng branch assignment mula sa admin. Once assigned, makikita mo dito ang menu.
                  </p>
                </div>
              );
            }
            return allNavItems.map((item, index) => (
              item.type === "divider" ? (
                <div key={index} className="pt-4 pb-2">
                  <p className="text-xs font-semibold text-white/50 uppercase tracking-wider px-3">
                    {item.label}
                  </p>
                </div>
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/" || item.path === "/inventory"}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? "active" : ""}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              )
            ));
          })()}
        </nav>
        
        {/* User Info & Footer - Fixed at bottom */}
        <div className="flex-shrink-0 border-t border-white/20 bg-inherit">
          {user && (
            <div className="p-3 border-b border-white/10">
              <div className="flex items-center gap-3 px-2">
                <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate" data-testid="user-name">
                    {user.name || user.email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-white/60 truncate capitalize" data-testid="user-role">
                    {user.role}
                    {((user.assigned_branches?.length > 0) || user.assigned_branch) && !user.is_admin && user.role !== "admin" && (
                      <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-[10px]">
                        {(user.assigned_branches?.length ? user.assigned_branches : [user.assigned_branch]).join(" + ")}
                      </span>
                    )}
                    {(user.is_admin || user.role === "admin") && (
                      <span className="ml-1 px-1.5 py-0.5 bg-green-500/30 rounded text-[10px]">
                        ALL ACCESS
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="p-3">
            <Button
              variant="ghost"
              className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
          
          <div className="px-4 pb-4">
            <p className="text-xs text-white/70 text-center font-medium">
              Orion PH Management System
            </p>
            <p className="text-xs text-white/50 text-center mt-1">
              © Orion PH – Internal Management System
            </p>
          </div>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="ml-64 flex-1 p-8 main-content" data-testid="main-content">
        {(() => {
          // ANTI-ABUSE: Newly-created users have no branch assigned yet.
          // Block ALL page content until admin assigns them a branch via User Management.
          const isAdmin = user?.role === "admin" || user?.is_admin === true || user?.can_access_all_branches === true;
          const hasBranch = !!user?.assigned_branch;
          const isPendingAccess = user && !isAdmin && !hasBranch;
          if (isPendingAccess) {
            return (
              <div className="min-h-[70vh] flex items-center justify-center" data-testid="pending-access-screen">
                <div className="max-w-md w-full bg-white border-2 border-amber-300 rounded-2xl p-8 shadow-lg">
                  <div className="flex justify-center mb-4">
                    <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center">
                      <Shield className="h-8 w-8 text-amber-600" />
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-center text-gray-900 mb-2">
                    Pending Branch Assignment
                  </h2>
                  <p className="text-sm text-center text-gray-600 mb-4">
                    Hi <strong>{user.name || user.email?.split('@')[0]}</strong>! Wala ka pang assigned branch sa system. Please contact your administrator para ma-grant ang access mo.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2 text-xs">
                    <p className="text-amber-900 font-medium">📋 Your account details:</p>
                    <p className="text-amber-800">Email: <code className="bg-white px-1 rounded">{user.email}</code></p>
                    <p className="text-amber-800">Role: <code className="bg-white px-1 rounded">{user.role || "pending"}</code></p>
                    <p className="text-amber-800">Branch: <span className="text-red-600 font-semibold">— (not assigned yet)</span></p>
                  </div>
                  <p className="text-[11px] text-center text-gray-500 mt-4">
                    Kung nakalimutan lang ng admin, message mo si Kyle (kyle@orionledlights.com) para ma-assign ka sa tamang branch.
                  </p>
                </div>
              </div>
            );
          }
          return <Outlet />;
        })()}
      </main>
    </div>
  );
}
